import { useCallback, useState } from "react";
import type {
    ActivityChartDensity,
    ShownActivityChartDensity,
} from "./activityChartDensity.lib";
import { toDensityView } from "./activityChartDensity.lib";

export interface StoredActivityChartDensity {
    stored: ActivityChartDensity | undefined;
    save: (density: ActivityChartDensity) => void;
}

export interface ActivityChartDensityControl {
    /** What the picker selects: picked on this page, else saved, else what the chart shows. */
    density: ActivityChartDensity;
    /** The finest density the chart fits the window to: the picked or saved one, else days. */
    closestDensity: ActivityChartDensity;
    /** What the chart shows: `closestDensity`, or a coarser one while the window doesn't fit it. */
    shownDensity: ActivityChartDensity;
    /** The line under the picker while the chart shows a coarser density than the picked or saved one. */
    hint: string | undefined;
    /** The person's pick, saved. */
    setDensity: (density: ActivityChartDensity) => void;
    /** The chart's fit for `closestDensity`, never saved. */
    showDensity: (density: ActivityChartDensity) => void;
}

/**
 * The chart's density: the one picked on this page, else the saved one,
 * else the finest one the window fits. The user caches read storage only on
 * mount, so a pick is kept here as well as saved.
 */
export function useActivityChartDensity({
    stored,
    save,
}: StoredActivityChartDensity): ActivityChartDensityControl {
    const [picked, setPicked] = useState<ActivityChartDensity>();
    const [reported, setReported] = useState<ShownActivityChartDensity>();
    const { closest, shown, selected, hint } = toDensityView({
        chosen: picked ?? stored,
        reported,
    });
    const setDensity = useCallback(
        (next: ActivityChartDensity) => {
            setPicked(next);
            save(next);
        },
        [save],
    );
    const showDensity = useCallback(
        (fitted: ActivityChartDensity) =>
            setReported({ closest, shown: fitted }),
        [closest],
    );
    return {
        density: selected,
        closestDensity: closest,
        shownDensity: shown,
        hint,
        setDensity,
        showDensity,
    };
}
